import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, Signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  BlockedWebViewComponent,
  GameDetailsComponent,
  PageLayoutComponent,
  TitileNavbarComponent
} from '@ui-components';
import { map, shareReplay, switchMap, tap } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MapInitializer } from '../../../../../find-games/src/lib/application/map-initializer';
import { JoinButtonsComponent } from '../join-buttons/join-buttons.component';
import {
  AppRoutes,
  CacheKey,
  GameDetailsModel,
  GamesRoutes,
  PLATFORM_TOKEN,
  PlatformProvider,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import { OpenSingleChatCommandHandler } from '@messages';
import { GameDetailsQueryHandler } from '../../application/handlers';
import { isAfter, parseISO, subMinutes } from 'date-fns';
import { UserRoleEnum } from '@core-http';

@Component({
  selector: 'lib-shared-game',
  templateUrl: './shared-game.component.html',
  styleUrls: ['./shared-game.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TitileNavbarComponent,
    JoinButtonsComponent,
    GameDetailsComponent,
    BlockedWebViewComponent,
    PageLayoutComponent
  ],
  providers: [OpenSingleChatCommandHandler]
})
export class SharedGameComponent {
  @ViewChild('map') map: ElementRef = {} as ElementRef;

  private readonly gameDetailsQueryHandler: GameDetailsQueryHandler = inject(GameDetailsQueryHandler);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly mapInitializer: MapInitializer = inject(MapInitializer);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly router: Router = inject(Router);
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly openSingleChatCommandHandler: OpenSingleChatCommandHandler = inject(OpenSingleChatCommandHandler);

  private readonly userData: Signal<UserModel | undefined> = toSignal(this.userDataProvider.userData$);

  readonly allowToView: Signal<boolean> = computed(() => {
    const userData: UserModel | undefined = this.userData();
    return userData?.role === UserRoleEnum.ADMIN || this.platformProvider.isNativeRun;
  });

  readonly gameDetails: Signal<GameDetailsModel | undefined> = toSignal(
    this.activatedRoute.params.pipe(
      switchMap((params: Params) => this.gameDetailsQueryHandler.getGameDetails(params['id'])),
      tap((game: GameDetailsModel) => {
        this.mapInitializer.initMap({
          mapContainerName: this.map?.nativeElement,
          startCoordinates: game.location.coordinates,
          zoom: 15
        });
        this.mapInitializer.addMarker({
          coordinates: game.location.coordinates,
          showCounter: false,
          isUserLocation: false
        });
      }),
      shareReplay(1)
    )
  );

  readonly redirectPath: Signal<string | undefined> = toSignal(
    this.activatedRoute.params.pipe(map((params: Params) => `${AppRoutes.GAMES}/${GamesRoutes.SHARED}/${params['id']}`))
  );

  applicationSent(gameId: string): void {
    this.router.navigateByUrl(`${AppRoutes.GAMES}/${GamesRoutes.DETAILS}/${gameId}`);
  }

  sendMessage(payload: { userId: string; displayName: string | undefined }): void {
    const redirectToGameFromChatUrl: string = this.router.url;
    sessionStorage.setItem(CacheKey.RedirectToGameFromChatUrl, redirectToGameFromChatUrl);

    this.openSingleChatCommandHandler.open(payload.userId, payload.displayName).subscribe();
  }

  allowToJoin(gameDateStart: string): boolean {
    const startDate: Date = parseISO(gameDateStart);
    const now: Date = new Date();
    const limit: Date = subMinutes(now, 30);

    return isAfter(startDate, limit);
  }
}
